"use client";

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { AnimatePresence, motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify-icon/react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import {
  getCurrencySymbol,
  getPriceTiers,
  PriceCurrency,
} from "@/features/calendar/types/pricing.tsx";
import { updateDefaultPricing } from "@/features/calendar/api/Pricing";
import { toast } from "@/utils/customToast";

const pricingSchema = (t: any) =>
  z.object({
    amount: z.number().min(0.01, t("forms:templates.validation.priceMinimium")).max(9999),
    currency: z.nativeEnum(PriceCurrency),
  });

type PricingFormValues = z.infer<ReturnType<typeof pricingSchema>>;

interface PricingFormProps {
  onComplete: () => void;
}

const pageVariants = {
  initial: {
    opacity: 0,
    y: 20,
  },
  in: {
    opacity: 1,
    y: 0,
  },
  out: {
    opacity: 0,
    y: -20,
  },
};

const pageTransition = {
  type: "tween",
  duration: 0.3,
};

const circleClassNames = {
  1: "-bottom-50 -left-40 size-90 from-green-500/20 to-blue-500/20 opacity-30",
  2: "-top-50 -right-40 size-90 from-purple-500/20 to-pink-500/20 opacity-30",
};

export function PricingForm({ onComplete }: PricingFormProps) {
  const { t } = useTranslation(["calendar", "forms", "common"]);

  const form = useForm<PricingFormValues>({
    resolver: zodResolver(pricingSchema(t)),
    defaultValues: {
      amount: undefined,
      currency: PriceCurrency.Usd,
    },
    mode: "onChange",
  });

  // Watch form values for changes
  const currency = form.watch("currency");

  // Form submission handler
  const onSubmit = async (values: PricingFormValues) => {
    try {
      await updateDefaultPricing({
        amount: values.amount,
        currency: values.currency,
      });
      onComplete();
      form.reset(values);
    } catch (error: any) {
      console.error("Failed to update default pricing:", error);

      // Handle specific error responses
      if (error.response?.data?.errors.amount[0]) {
        const minPrice = error.response?.data?.min_price || 10;

        form.setError("amount", {
          type: "manual",
          message: t("calendar:pricing.minimumPriceError", {
            symbol: getCurrencySymbol(PriceCurrency.Usd),
            price: minPrice,
          }),
        });
      } else if (error.response?.status === 422) {
        toast.error(t("common:errors.invalidPricingData"), {
          description: t("common:errors.checkPriceAndCurrency"),
        });
      } else if (error.response?.status === 403) {
        toast.error(t("common:errors.permissionDenied"), {
          description: t("common:errors.noPermissionToUpdatePricing"),
        });
      } else {
        toast.error(t("calendar:pricing.failedToUpdateDefaultPricing"), {
          description: t("common:errors.tryAgainOrContactSupport"),
        });
      }
    }
  };

  // Handle price tier selection
  const handlePriceTierClick = (price: number) => {
    form.setValue("amount", price, { shouldDirty: true, shouldValidate: true });
    form.clearErrors("amount");
  };

  return (
    <Form {...form}>
      <motion.form
        animate={{ opacity: 1 }}
        className="bg-foreground/50 relative my-2 flex w-full overflow-hidden rounded-xl backdrop-blur-xl sm:my-8 sm:rounded-2xl"
        initial={{ opacity: 0 }}
        transition={{ duration: 0.75 }}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        {/* Background circles */}
        <motion.div
          layout
          animate={{ opacity: 0.15 }}
          className={`absolute ${circleClassNames["1"]} duration-0.75 rounded-full bg-gradient-to-br opacity-15 transition-colors`}
          initial={{ opacity: 0 }}
          layoutId="circle1"
          transition={{
            default: { duration: 0.75 },
            layout: { duration: 0.75 },
          }}
        />
        <motion.div
          layout
          animate={{ opacity: 0.15 }}
          className={`absolute ${circleClassNames["2"]} duration-0.75 rounded-full bg-gradient-to-br opacity-15 transition-colors`}
          initial={{ opacity: 0.15 }}
          layoutId="circle2"
          transition={{
            layout: { duration: 0.75 },
          }}
        />

        {/* Main content */}
        <div className="relative z-50 flex w-full items-center justify-center py-8">
          <AnimatePresence mode="wait">
            <motion.div
              animate="in"
              className="flex w-full items-center justify-center p-3 sm:p-4"
              transition={pageTransition}
              variants={pageVariants}
            >
              <div className="w-full max-w-2xl space-y-4">
                {/* Header */}
                <div className="text-center">
                  <div className="bg-muted/30 mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full">
                    <Icon
                      className="text-accent-foreground"
                      height="1.5rem"
                      icon="mdi:cash-multiple"
                      width="1.5rem"
                    />
                  </div>
                  <h1 className="text-accent-foreground text-lg font-bold sm:text-xl md:text-2xl">
                    {t("calendar:pricing.defaultPricing", "Set Your Default Pricing")}
                  </h1>
                  <p className="text-muted-foreground mt-1 text-xs sm:text-sm">
                    {t(
                      "calendar:pricing.defaultPricingDescription",
                      "Set your default lesson pricing",
                    )}
                  </p>
                </div>

                {/* Pricing Form */}
                <div className="space-y-3">
                  <div className="flex flex-col space-y-1.5">
                    <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-0">
                      <label
                        className="text-accent-foreground text-xs font-medium sm:text-sm"
                        htmlFor="default-price"
                      >
                        {t("calendar:pricing.pricePerLesson", "Price per lesson")}
                      </label>
                      <FormField
                        control={form.control}
                        name="currency"
                        render={({ field }) => (
                          <FormItem>
                            <div className="bg-muted hover:bg-side-accent flex h-7 items-center self-start rounded-full px-2 sm:h-8 sm:self-auto sm:px-3">
                              <Select
                                disabled={form.formState.isSubmitting}
                                value={field.value}
                                onValueChange={(value) => {
                                  field.onChange(value);
                                  form.clearErrors("amount");
                                }}
                              >
                                <FormControl>
                                  <SelectTrigger className="h-7 min-w-[60px] border-0 bg-transparent p-0 px-1 text-xs shadow-none focus:ring-0 focus:ring-offset-0 sm:h-8 sm:min-w-[80px] sm:px-2 sm:text-sm">
                                    <SelectValue />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent align="center" className="z-50">
                                  <SelectItem value={PriceCurrency.Eur}>
                                    <div className="flex items-center gap-1 sm:gap-2">
                                      <span className="text-xs">€</span>
                                      <span className="text-xs sm:text-sm">EUR</span>
                                    </div>
                                  </SelectItem>
                                  <SelectItem value={PriceCurrency.Gbp}>
                                    <div className="flex items-center gap-1 sm:gap-2">
                                      <span className="text-xs">£</span>
                                      <span className="text-xs sm:text-sm">GBP</span>
                                    </div>
                                  </SelectItem>
                                  <SelectItem value={PriceCurrency.Pln}>
                                    <div className="flex items-center gap-1 sm:gap-2">
                                      <span className="text-xs">zł</span>
                                      <span className="text-xs sm:text-sm">PLN</span>
                                    </div>
                                  </SelectItem>
                                  <SelectItem value={PriceCurrency.Usd}>
                                    <div className="flex items-center gap-1 sm:gap-2">
                                      <span className="text-xs">$</span>
                                      <span className="text-xs sm:text-sm">USD</span>
                                    </div>
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="amount"
                      render={({ field }) => (
                        <FormItem>
                          <div className="bg-muted/20 group relative rounded-md backdrop-blur-sm">
                            <div className="absolute top-1/2 left-0 flex h-12 w-12 -translate-y-1/2 items-center justify-center bg-transparent transition-colors sm:w-16">
                              <span className="text-accent-foreground text-lg font-semibold sm:text-xl">
                                {getCurrencySymbol(currency)}
                              </span>
                            </div>
                            <FormControl>
                              <Input
                                className={`hover:bg-muted/50 focus:bg-muted/10 h-12 appearance-none border-0 bg-transparent pl-12 text-base font-semibold transition-colors sm:h-14 sm:pl-16 lg:text-lg [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none ${
                                  form.formState.errors.amount ? "border border-red-500" : ""
                                }`}
                                disabled={form.formState.isSubmitting}
                                id="default-price"
                                min="0.01"
                                placeholder={t(
                                  "calendar:pricing.yourLessonPrice",
                                  "Your lesson price",
                                )}
                                step="0.01"
                                type="number"
                                value={field.value === undefined ? "" : field.value}
                                onChange={(e) => {
                                  const value =
                                    e.target.value === ""
                                      ? undefined
                                      : Number.parseFloat(e.target.value);

                                  if (value !== undefined && value > 9999) return;
                                  field.onChange(value);
                                }}
                              />
                            </FormControl>
                          </div>
                          <FormMessage>
                            {form.formState.errors.amount && (
                              <div className="flex items-center gap-1 text-red-500">
                                <Icon height="1rem" icon="mdi:alert-circle-outline" width="1rem" />
                                <span>{form.formState.errors.amount.message}</span>
                              </div>
                            )}
                          </FormMessage>
                        </FormItem>
                      )}
                    />

                    {/* Price Tiers */}
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={`price-tiers-${currency}`}
                        animate={{ opacity: 1 }}
                        className="mt-4 space-y-2"
                        exit={{ opacity: 0 }}
                        initial={{ opacity: 0 }}
                      >
                        <h4 className="text-muted-foreground text-sm font-medium">
                          {t("calendar:pricing.suggestedPrices", "Suggested prices")}
                        </h4>

                        {/* Mobile: 3 + 2 layout */}
                        <div className="space-y-2 md:hidden">
                          <div className="grid grid-cols-3 gap-3">
                            {getPriceTiers(currency)
                              .slice(0, 3)
                              .map((price) => (
                                <button
                                  key={`price-${price}`}
                                  className="bg-muted hover:bg-side-accent flex h-10 items-center justify-center rounded-md text-xs font-medium transition-colors sm:text-sm md:text-base"
                                  type="button"
                                  onClick={() => handlePriceTierClick(price)}
                                >
                                  {getCurrencySymbol(currency)}
                                  {price}
                                </button>
                              ))}
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            {getPriceTiers(currency)
                              .slice(3)
                              .map((price) => (
                                <button
                                  key={`price-${price}`}
                                  className="bg-muted hover:bg-side-accent flex h-10 items-center justify-center rounded-md text-xs font-medium transition-colors sm:text-sm md:text-base"
                                  type="button"
                                  onClick={() => handlePriceTierClick(price)}
                                >
                                  {getCurrencySymbol(currency)}
                                  {price}
                                </button>
                              ))}
                          </div>
                        </div>

                        {/* Desktop/tablet: single row when it fits */}
                        <div className="hidden md:grid md:grid-cols-5 md:gap-3">
                          {getPriceTiers(currency).map((price) => (
                            <button
                              key={`price-${price}`}
                              className="bg-muted hover:bg-side-accent flex h-10 items-center justify-center rounded-md text-xs font-medium transition-colors sm:text-sm md:text-base"
                              type="button"
                              onClick={() => handlePriceTierClick(price)}
                            >
                              {getCurrencySymbol(currency)}
                              {price}
                            </button>
                          ))}
                        </div>
                      </motion.div>
                    </AnimatePresence>
                  </div>

                  {/* Submit Button */}
                  <div className="flex justify-center pt-1">
                    <Button
                      className="flex items-center gap-2 max-[400px]:w-full"
                      disabled={form.formState.isSubmitting || !form.formState.isValid}
                      size="lg"
                      type="submit"
                      variant="foreground"
                    >
                      {form.formState.isSubmitting ? (
                        <>
                          <Icon
                            className="animate-spin"
                            height="1rem"
                            icon="mdi:loading"
                            width="1rem"
                          />
                          {t("common:saving", "Saving...")}
                        </>
                      ) : (
                        <>
                          <Icon height="1rem" icon="mdi:content-save-outline" width="1rem" />
                          {t("common:save", "Save Pricing")}
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </motion.form>
    </Form>
  );
}
